import React, { useRef, useEffect, useState, Children, cloneElement, isValidElement, RefAttributes } from "react";
import { twMerge, twJoin } from "tailwind-merge";

import { SupScrollable } from "../../../../components/UI/Scrollable";
import SupChevronLeft from "../../../../icons/ChevronLeft";
import SupChevronRight from "../../../../icons/ChevronRight";
import SupChevronUp from "../../../../icons/ChevronUp";
import SupChevronDown from "../../../../icons/ChevronDown";

import { type TabListProps, SupTabList } from "./TabList";
import { type TabProps } from "../Tab";

interface ScrollableTabListProps extends TabListProps {
  delta?: number;
}

export function SupScrollableTabList({ orientation = "horizontal", children, delta = 50, className, ...restProps }: ScrollableTabListProps) {
  const scrollRootRef = useRef<HTMLDivElement>(null);
  const startRef = useRef<HTMLSpanElement>(null);
  const endRef = useRef<HTMLSpanElement>(null);

  const [displayStartScroll, setDisplayStartScroll] = useState(false);
  const [displayEndScroll, setDisplayEndScroll] = useState(false);

  const isHorizontal = orientation === "horizontal";

  const tabItemCounts = Children.count(children);
  const tabItems = Children.map(children, (child, i) => {
    if (!isValidElement<TabProps & RefAttributes<Element>>(child)) return null;

    if (i === 0) {
      return cloneElement(child, { ref: startRef });
    }

    if (i === tabItemCounts - 1) {
      return cloneElement(child, { ref: endRef });
    }

    return child;
  });

  useEffect(function initIntersectionObserver() {
    const root = scrollRootRef.current;
    const begin = startRef.current;
    const end = endRef.current;

    if (!root || !begin || !end) {
      return;
    }

    const observer = new IntersectionObserver(
      entries => {
        for (const entry of entries) {
          if (entry.target === begin) {
            setDisplayStartScroll(!entry.isIntersecting);
          }

          if (entry.target === end) {
            setDisplayEndScroll(!entry.isIntersecting);
          }
        }
      },
      { root, threshold: 0.9 }
    );

    observer.observe(begin);
    observer.observe(end);

    return () => {
      observer.unobserve(begin);
      observer.unobserve(end);
    };
  }, []);

  const handleStartClick = () => {
    const scrollWrapper = scrollRootRef.current;

    if (!scrollWrapper) return;

    if (orientation === "horizontal") {
      scrollWrapper.scrollLeft -= delta;
    } else {
      scrollWrapper.scrollTop -= delta;
    }
  };

  const handleEndClick = () => {
    const scrollWrapper = scrollRootRef.current;

    if (!scrollWrapper) return;

    if (orientation === "horizontal") {
      scrollWrapper.scrollLeft += delta;
    } else {
      scrollWrapper.scrollTop += delta;
    }
  };

  return (
    <div className={twMerge("sup-srollable relative flex items-center", isHorizontal ? "max-w-full" : "flex-col max-h-full", className)}>
      <button
        className={twJoin("w-5 h-5 shrink-0 transition-colors duration-200 text-neutral-900 hover:text-neutral-950", displayStartScroll ? "visible" : "invisible")}
        onClick={handleStartClick}
      >
        {isHorizontal ? <SupChevronLeft /> : <SupChevronUp />}
      </button>

      <SupScrollable ref={scrollRootRef} noScrollbar orientation={orientation} className="w-full">
        <SupTabList orientation={orientation} {...restProps}>
          {tabItems}
        </SupTabList>
      </SupScrollable>

      <button
        className={twJoin("w-5 h-5 shrink-0 transition-colors duration-200 text-neutral-900 hover:text-neutral-950", displayEndScroll ? "visible" : "invisible")}
        onClick={handleEndClick}
      >
        {isHorizontal ? <SupChevronRight /> : <SupChevronDown />}
      </button>
    </div>
  );
}
